import { useState, type KeyboardEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@diwata/ironvale'
import type { GraphAPI } from '@/api/graph'
import { identityFields, mergeIdentity } from '@/lib/onboarding'
import './identity.css'

// The identity-kernel editor — the "area to update memory/identity kernel" on the ego dossier.
// It VIEWS and EDITS the identity-kernel mirror carried on the ego entity's
// attributes.identity, mirroring packages/identity-kernel:
//   t0 { name, tagline }  ·  t1 { professional_summary, expertise[] }  ·  t3 { personal_goals[] }
//
// T4 DOCTRINE: finance, credentials, legal/medical are NEVER asked and NEVER written by any
// Diwata surface — this editor has no such field and must never gain one. (See identity/tiers.py.)

/** A small human-text chip editor: click a chip to remove, type + Enter to add. Values are kept
 *  verbatim (trimmed) — expertise and goals are prose, not snake_case vocabulary words. */
function ChipEditor({
  chips,
  onChange,
  placeholder,
  max,
}: {
  chips: string[]
  onChange: (next: string[]) => void
  placeholder: string
  max?: number
}) {
  const [draft, setDraft] = useState('')
  function add() {
    const chip = draft.trim()
    if (chip && !chips.includes(chip) && (max === undefined || chips.length < max)) {
      onChange([...chips, chip])
    }
    setDraft('')
  }
  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      add()
    }
  }
  return (
    <div className="dw-idk-chips">
      {chips.map((chip) => (
        <button
          key={chip}
          type="button"
          className="dw-idk-chip"
          title={`Remove ${chip}`}
          onClick={() => onChange(chips.filter((c) => c !== chip))}
        >
          {chip}
          <span aria-hidden="true"> ×</span>
        </button>
      ))}
      <input
        className="dw-idk-chip-input"
        value={draft}
        placeholder={chips.length === 0 ? placeholder : '+ add'}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={add}
      />
    </div>
  )
}

export function IdentityEditor({
  egoId,
  identity,
  graphApi,
  onSaved,
  hideTitle = false,
}: {
  egoId: string
  /** The ego node's current attributes.identity — any shape; read tolerantly via identityFields. */
  identity: unknown
  graphApi: GraphAPI
  /** Reflect the merged identity locally (optimistic) so the view updates before the refetch lands. */
  onSaved: (identity: Record<string, unknown>) => void
  /** Suppress the internal "About you" title (keep the edit pencil) — for surfaces like the
   *  Profile page that already title this section in their own card header. */
  hideTitle?: boolean
}) {
  const qc = useQueryClient()
  const view = identityFields(identity)

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState(view.name)
  const [tagline, setTagline] = useState(view.tagline)
  const [summary, setSummary] = useState(view.summary)
  const [expertise, setExpertise] = useState<string[]>(view.expertise)
  const [goals, setGoals] = useState<string[]>(view.goals)

  const isEmpty =
    !view.name && !view.tagline && !view.summary && view.expertise.length === 0 && view.goals.length === 0

  function beginEdit() {
    // Seed the draft from the freshest view every time — a save/refetch may have moved it.
    setName(view.name)
    setTagline(view.tagline)
    setSummary(view.summary)
    setExpertise(view.expertise)
    setGoals(view.goals)
    setError(null)
    setEditing(true)
  }

  /** The tiers this editor is authoritative for. Only these fields are overwritten; mergeIdentity
   *  overlays them onto the CURRENT tiers, so untouched kernel fields (t1.current_role, the whole
   *  t2 internal tier, …) survive. No t4: finance/credentials are never collected here. */
  function editedTiers(): Record<string, unknown> {
    return {
      t0: { name: name.trim(), tagline: tagline.trim() },
      t1: { professional_summary: summary.trim(), expertise },
      t3: { personal_goals: goals },
    }
  }

  // READ-MERGE-WRITE: the PATCH replaces the `identity` attribute key wholesale, so a partial edit
  // would wipe every tier it didn't send. We fetch the ego's CURRENT identity fresh at save time,
  // merge the edited tiers onto it, and PATCH the whole object back. Optimistic + invalidate.
  async function save() {
    setSaving(true)
    setError(null)
    try {
      const net = await graphApi.network() // fresh read — not the possibly-stale prop snapshot
      const ego = net.nodes.find((n) => n.id === egoId) ?? net.nodes.find((n) => n.self)
      const current = ego?.attributes?.identity
      const merged = mergeIdentity(current, editedTiers())
      await graphApi.patchEntityAttributes(egoId, { identity: merged })
      onSaved(merged) // optimistic local reflection
      qc.invalidateQueries({ queryKey: ['graph-network'] })
      qc.invalidateQueries({ queryKey: ['dossier', egoId] })
      qc.invalidateQueries({ queryKey: ['onboarding-ego-identity'] })
      setEditing(false)
    } catch {
      setError('Could not save. Your edits are kept — try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="dw-idk-head" style={hideTitle ? { marginTop: 0 } : undefined}>
        {hideTitle ? (
          <span />
        ) : (
          <div className="dw-rail-section-title" style={{ marginTop: 0 }}>About you</div>
        )}
        {!editing && (
          <button
            type="button"
            className="dw-dossier-edit"
            aria-label="Edit your identity"
            title="Edit"
            onClick={beginEdit}
          >
            ✎
          </button>
        )}
      </div>

      {!editing ? (
        <div className="dw-idk-view">
          {isEmpty ? (
            <div className="dw-idk-value dw-idk-value--empty">
              Tala doesn&rsquo;t know much about you yet. Add a few details so your familiar can serve you better.
            </div>
          ) : (
            <>
              <Field label="Name" value={view.name} />
              <Field label="What you do" value={view.tagline} />
              <Field label="Your work" value={view.summary} />
              <TagRow label="Expertise" tags={view.expertise} />
              <TagRow label="Building toward" tags={view.goals} />
            </>
          )}
          <div className="dw-idk-note">Tala never asks for financial or credential details.</div>
        </div>
      ) : (
        <form
          className="dw-idk-form"
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <div className="dw-idk-field">
            <label className="dw-idk-label" htmlFor="dw-idk-name">Name</label>
            <Input
              id="dw-idk-name"
              autoFocus
              value={name}
              placeholder="What should Tala call you?"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="dw-idk-field">
            <label className="dw-idk-label" htmlFor="dw-idk-tagline">What you do</label>
            <Input
              id="dw-idk-tagline"
              value={tagline}
              placeholder="e.g. founder · investor · researcher"
              onChange={(e) => setTagline(e.target.value)}
            />
          </div>

          <div className="dw-idk-field">
            <label className="dw-idk-label" htmlFor="dw-idk-summary">Your work, in a sentence</label>
            <textarea
              id="dw-idk-summary"
              className="dw-idk-textarea"
              value={summary}
              placeholder="e.g. I run a two-person studio building tools for analysts."
              onChange={(e) => setSummary(e.target.value)}
            />
          </div>

          <div className="dw-idk-field">
            <span className="dw-idk-label">Expertise <span style={{ textTransform: 'none', letterSpacing: 0 }}>(up to 5)</span></span>
            <ChipEditor chips={expertise} onChange={setExpertise} placeholder="e.g. capital markets, Python…" max={5} />
          </div>

          <div className="dw-idk-field">
            <span className="dw-idk-label">Building toward</span>
            <ChipEditor chips={goals} onChange={setGoals} placeholder="e.g. raise a seed round, ship v1…" />
          </div>

          <div className="dw-idk-note">Tala never asks for financial or credential details.</div>
          {error && <div className="dw-idk-error">{error}</div>}

          <div className="dw-idk-actions">
            <Button size="sm" type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button size="sm" type="button" variant="ghost" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="dw-idk-row">
      <div className="dw-idk-label">{label}</div>
      {value ? (
        <div className="dw-idk-value">{value}</div>
      ) : (
        <div className="dw-idk-value dw-idk-value--empty">Not set</div>
      )}
    </div>
  )
}

function TagRow({ label, tags }: { label: string; tags: string[] }) {
  return (
    <div className="dw-idk-row">
      <div className="dw-idk-label">{label}</div>
      {tags.length > 0 ? (
        <div className="dw-idk-tags">
          {tags.map((t) => (
            <span key={t} className="dw-idk-tag">{t}</span>
          ))}
        </div>
      ) : (
        <div className="dw-idk-value dw-idk-value--empty">Not set</div>
      )}
    </div>
  )
}
